import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { appConfig } from "@/utils/config";
import { endpoints } from "./endpoints";
import { tokenStorage } from "./tokenStorage";
import { tokenTransport } from "./tokenTransport";
import { authEvents } from "./authEvents";

/**
 * Single shared Axios instance. All service modules route through this
 * client so the base URL and auth header injection stay in one place.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((requestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Sprint: React Authentication Foundation. Shared across every concurrent
// 401 so N simultaneous requests trigger exactly ONE `/auth/refresh` call,
// not N of them.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    return null;
  }
  try {
    const tokens = await tokenTransport.refresh(refreshToken);
    tokenStorage.setTokens(tokens);
    return tokens.accessToken;
  } catch {
    // Refresh token itself is invalid/expired/revoked - the session is
    // genuinely over. Clear local credentials and let AuthContext react
    // (redirect to /login) rather than leaving stale tokens behind.
    tokenStorage.clearTokens();
    authEvents.emitSessionExpired();
    return null;
  }
}

function isAuthEndpoint(url: string | undefined): boolean {
  return url === endpoints.auth.login || url === endpoints.auth.refresh;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Never attempt a refresh in response to `/auth/login` or
    // `/auth/refresh` itself failing - that would recurse straight back
    // into this same handler and loop.
    if (isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    // Already retried once for this request and still unauthorized -
    // stop here rather than retrying forever.
    if (originalRequest._retry) {
      tokenStorage.clearTokens();
      authEvents.emitSessionExpired();
      return Promise.reject(error);
    }

    const method = (originalRequest.method ?? "get").toLowerCase();
    const isSafeToRetry = method === "get" || method === "head";

    if (!isSafeToRetry) {
      // A mutating request (order submission, sync trigger, snapshot
      // generation, ...) must never be silently re-issued after a 401 -
      // replaying it blindly risks a duplicate side effect (e.g. a
      // double-submitted order). Refresh the token in the background so
      // the caller's *next* attempt succeeds, but surface this failure
      // to the caller now so the UI decides whether to resubmit.
      refreshInFlight ??= refreshAccessToken().finally(() => {
        refreshInFlight = null;
      });
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    refreshInFlight ??= refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
    const newAccessToken = await refreshInFlight;

    if (!newAccessToken) {
      return Promise.reject(error);
    }

    originalRequest.headers.set("Authorization", `Bearer ${newAccessToken}`);
    return apiClient.request(originalRequest);
  },
);
